import { Box, Stack, TextField, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import {
  COLLECTED_DETAILS,
  type NextTask,
} from '@/north-star/NorthStar.fixtures';

// The drawer for a next-step: it names what the task is part of, then gives a
// way to actually do it — photo capture, a question, an invite, or a review of
// what Keni collected. Bottom sheet over the phone screen. All inputs are mock.
export type TaskDrawerProps = {
  task: NextTask;
  onClose: () => void;
  onDone: (id: string) => void;
};

// A dashed tile standing in for the camera / photo capture.
const CaptureTile = () => (
  <Stack
    spacing={1}
    sx={{
      alignItems: 'center',
      justifyContent: 'center',
      py: 5,
      borderRadius: `${radius.lg}px`,
      color: 'custom.coral.700',
      border: '2px dashed',
      borderColor: 'custom.coral.100',
    }}
  >
    <Icon icon={'camera' as never} fontSize={28} />
    <Typography variant='caption' color='text.secondary'>
      Point at the prescription label
    </Typography>
  </Stack>
);

const Questions = () => (
  <TextField
    fullWidth
    size='small'
    label='National Insurance number'
    placeholder='QQ 12 34 56 C'
  />
);

const Invite = () => (
  <Stack spacing={1.5}>
    <TextField
      fullWidth
      size='small'
      label='Their name'
      placeholder='e.g. Sarah'
    />
    <TextField
      fullWidth
      size='small'
      label='Their email'
      type='email'
      placeholder='name@email.com'
    />
  </Stack>
);

const Review = () => (
  <Stack
    sx={{
      border: 1,
      borderColor: 'custom.neutral.200',
      borderRadius: `${radius.lg}px`,
      overflow: 'hidden',
    }}
    divider={<Box sx={{ height: '1px', bgcolor: 'custom.neutral.200' }} />}
  >
    {COLLECTED_DETAILS.map((d) => (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          bgcolor: 'background.paper',
        }}
        key={d.label}
        direction='row'
      >
        <Typography variant='caption' color='text.secondary'>
          {d.label}
        </Typography>
        <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
          <Typography variant='subtitle2'>{d.value}</Typography>
          <Box sx={{ display: 'flex', color: 'text.disabled' }}>
            <Icon icon={'pen-to-square' as never} fontSize={12} />
          </Box>
        </Stack>
      </Stack>
    ))}
  </Stack>
);

const bodyFor = (kind: NextTask['kind']) => {
  if (kind === 'capture') return <CaptureTile />;
  if (kind === 'questions') return <Questions />;
  if (kind === 'invite') return <Invite />;
  return <Review />;
};

const TaskDrawer = ({ task, onClose, onDone }: TaskDrawerProps) => (
  <BottomSheet
    title={task.title}
    variant='full'
    onClose={onClose}
    footer={(close) => (
      <Button
        variant='primary'
        size='lg'
        isFullWidth
        ariaLabel={task.cta}
        onPress={() => {
          onDone(task.id);
          close();
        }}
      >
        {task.cta}
      </Button>
    )}
  >
    <Stack spacing={2.5}>
      {/* what this is from */}
      <Stack
        sx={{
          alignItems: 'center',
          px: 2,
          py: 1.25,
          borderRadius: `${radius.md}px`,
          bgcolor: 'custom.coral.50',
          color: 'custom.coral.700',
        }}
        direction='row'
        spacing={1}
      >
        <Icon icon={'circle-info' as never} fontSize={14} />
        <Typography variant='caption' color='custom.coral.700'>
          {task.from}
        </Typography>
      </Stack>

      <Typography variant='body2' color='text.secondary'>
        {task.body}
      </Typography>

      {bodyFor(task.kind)}
    </Stack>
  </BottomSheet>
);

TaskDrawer.displayName = 'TaskDrawer';
export default TaskDrawer;
